/**
 * @fileoverview "Page fit" for header/footer letterhead artwork (MOR-1431)
 *
 * The crop editor saves header/footer crops PAGE-WIDE: the image spans the
 * full source page width, so the artwork carries its true on-page position
 * (including the firm's own side margins, as white space).
 *
 * Historically every renderer drew that image at `width:100%` of a slot that
 * is inset by the zone margins (56px a side by default) and by any
 * full-height side band. The whole page image was therefore squeezed — a
 * normal letterhead came out ~86% size with its margins doubled, and firms
 * had to rebuild their letterhead edge to edge to compensate. Because the
 * squeeze depends on the slot width, editing margins also rescaled the
 * artwork and moved where the body starts.
 *
 * A page-fit zone is instead drawn at page scale, anchored to the page's
 * left edge, at the vertical position it was cropped from. Zone margins no
 * longer apply to it (the artwork already sits where the firm put it) and a
 * side band clips it rather than squeezing it, so header and rail share one
 * scale and line up.
 *
 * Page fit is opt-in per asset (`letterheadAssets.imageFit === "page"`), set
 * when a crop is saved from the editor. Assets without it keep slot fit, so
 * nothing already configured moves.
 *
 * Used by every surface that lays out a header/footer slot: the PDF/Word
 * exporter, the paginated preview, and the branding sample preview.
 */

import {
  PAGE_NUMBER_BAND_PX,
  footerBottomPaddingWithPageNumber,
} from "./page-numbering";
import {
  pickVariantForPage,
  symmetricSide,
  type DocumentPadding,
  type VariantFragments,
} from "./resolve-letterhead";

/** A4 at 96dpi — the page every renderer lays out in. */
export const PAGE_FIT_WIDTH_PX = 794;
export const PAGE_FIT_HEIGHT_PX = 1123;

/** How a header/footer image occupies its zone. Absent means "slot". */
export type ImageFit = "slot" | "page";

/**
 * Page-fit state of the header and footer showing on one page. Each value is
 * a fraction of the page height — the blank space above the header crop /
 * below the footer crop — or `null` when that zone is slot-fit (or absent).
 */
export interface ZonePageFit {
  headerTop: number | null;
  footerBottom: number | null;
}

export const NO_PAGE_FIT: Readonly<ZonePageFit> = {
  headerTop: null,
  footerBottom: null,
};

function clampFraction(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
}

/**
 * The page-fit offset an asset renders with, or `null` for slot fit.
 *
 * @param zone      Which zone the asset fills.
 * @param imageFit  The asset's stored fit.
 * @param selection Where the crop was taken on the source page (fractions).
 */
export function pageFitOffset(
  zone: "header" | "footer",
  imageFit: string | null | undefined,
  selection: { top: number; bottom: number } | null | undefined,
): number | null {
  if (imageFit !== "page") return null;
  if (!selection) return 0;
  return zone === "header"
    ? clampFraction(selection.top)
    : clampFraction(1 - selection.bottom);
}

/** Page-fit state from the fragments that won the header / footer zone. */
export function zonePageFit(
  headerVariant: VariantFragments | null | undefined,
  footerVariant: VariantFragments | null | undefined,
): ZonePageFit {
  return {
    headerTop: headerVariant?.headerPageFitTop ?? null,
    footerBottom: footerVariant?.footerPageFitBottom ?? null,
  };
}

/**
 * Zones whose margins don't apply on a page because the artwork there is
 * page-fit. Margin editors lock these so a control never silently does
 * nothing.
 */
export interface ZoneLocks {
  header: boolean;
  footer: boolean;
}

export function zoneLocksForPage(
  variants: VariantFragments[],
  pageIndex: number,
): ZoneLocks {
  const fit = zonePageFit(
    pickVariantForPage(variants, "header", pageIndex),
    pickVariantForPage(variants, "footer", pageIndex),
  );
  return {
    header: fit.headerTop !== null,
    footer: fit.footerBottom !== null,
  };
}

/** Shown beside a locked zone's margin controls. */
export const PAGE_FIT_LOCK_NOTE =
  "Set by the letterhead. This artwork keeps its position on the page, so these margins don't apply. To change that, edit the letterhead's crop.";

/**
 * The padding a page's header/footer SLOTS render with. A page-fit zone
 * drops its side margins and takes its vertical offset from the crop's own
 * position on the source page; body margins are never touched, which is
 * what decouples them from the letterhead.
 */
export function applyPageFitToPadding(
  padding: DocumentPadding,
  fit: ZonePageFit,
): DocumentPadding {
  if (fit.headerTop === null && fit.footerBottom === null) return padding;
  const header =
    fit.headerTop === null
      ? {}
      : {
          headerTop: Math.round(fit.headerTop * PAGE_FIT_HEIGHT_PX),
          headerLeft: 0,
          headerRight: 0,
          headerSide: symmetricSide(0, 0),
        };
  const footer =
    fit.footerBottom === null
      ? {}
      : {
          footerBottom: Math.round(fit.footerBottom * PAGE_FIT_HEIGHT_PX),
          footerLeft: 0,
          footerRight: 0,
          footerSide: symmetricSide(0, 0),
        };
  return { ...padding, ...header, ...footer };
}

/** Vertical padding of a footer slot, in px. */
export interface FooterSlotInsets {
  topPx: number;
  bottomPx: number;
}

/**
 * Where a footer slot's artwork sits vertically on a page that may also
 * show a page number. Both cases reserve the same extra band, so the
 * paginator's `footerHeight + PAGE_NUMBER_BAND_PX` holds for either:
 *
 *   - slot fit: the number line sits BELOW the letterhead, so the artwork
 *     is lifted by the band (the historical behaviour).
 *   - page fit: the artwork must stay where the firm's letterhead put it
 *     (often flush with the page bottom), so the band goes ABOVE it and the
 *     number sits there — see `pageNumberBottomPx`.
 *
 * @param slotPadding Padding with page fit already applied.
 */
export function footerSlotInsets(
  slotPadding: DocumentPadding,
  pageFitBottom: number | null | undefined,
  showsPageNumber: boolean,
): FooterSlotInsets {
  const isPageFit = pageFitBottom !== null && pageFitBottom !== undefined;
  return isPageFit
    ? {
        topPx: showsPageNumber ? PAGE_NUMBER_BAND_PX : 0,
        bottomPx: slotPadding.footerBottom,
      }
    : {
        topPx: 0,
        bottomPx: footerBottomPaddingWithPageNumber(
          slotPadding.footerBottom,
          showsPageNumber,
        ),
      };
}

/**
 * Distance of the page-number line from the page bottom. Beside a page-fit
 * footer it clears the artwork (whose full height, offset included, is the
 * measured footer height); otherwise it sits at the configured footer
 * bottom margin, under the letterhead.
 */
export function pageNumberBottomPx(
  padding: DocumentPadding,
  pageFitBottom: number | null | undefined,
  footerHeightPx: number,
): number {
  return pageFitBottom !== null && pageFitBottom !== undefined
    ? footerHeightPx
    : padding.footerBottom;
}

/**
 * Wrap a page-fit zone's image HTML so it lays out at page width no matter
 * how narrow its slot is. `slotLeftInsetPx` is how far the slot itself
 * starts from the page's left edge (a full-height side band or topBlock);
 * the negative margin pulls the artwork back to the page edge and the
 * slot's own `overflow:hidden` clips whatever falls under the band.
 *
 * The wrapper is numeric-only markup, so it is safe to add around HTML that
 * has already been sanitised.
 */
export function wrapPageFitHtml(html: string, slotLeftInsetPx = 0): string {
  const inset = Math.max(0, Math.round(slotLeftInsetPx));
  const margin = inset > 0 ? `margin-left:-${inset}px;` : "";
  return `<div style="width:${PAGE_FIT_WIDTH_PX}px;${margin}">${html}</div>`;
}

/** `wrapPageFitHtml` when the zone is page-fit, the HTML unchanged otherwise. */
export function fitZoneHtml(
  html: string,
  offset: number | null | undefined,
  slotLeftInsetPx = 0,
): string {
  return offset === null || offset === undefined
    ? html
    : wrapPageFitHtml(html, slotLeftInsetPx);
}
